/**
 * Search command dialog for the app shell.
 * Mirrors grouped sidebar history inside a centered command palette.
 */

import * as React from 'react'
import { Command as CommandPrimitive } from 'cmdk'
import { FilePlus, Search, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AgentIcon } from '@/components/agents/agent-icon'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { useSidebar } from '@/components/ui/sidebar'
import { useChatHistory } from '@/hooks/use-chat-history'
import { SUPER_AGENT_ID, isSuperAgentId } from '@taqat/core/super-agent'
import { ChatRowIcon, ChatRowTitle, useChatRowLiveState } from './chat-row-live-state'
import type { ChatHistoryApiItem, GroupedChatData } from '@taqat/core'

const SEARCH_LIMIT = 50
const GROUP_ORDER: Array<keyof GroupedChatData> = ['today', 'yesterday', 'lastWeek', 'lastMonth', 'older']

const GROUP_LABELS: Record<keyof GroupedChatData, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  lastWeek: 'Last 7 days',
  lastMonth: 'Last 30 days',
  older: 'Older',
}

interface SidebarChatAgent {
  id: string
  name: string
  avatar?: string | null
}

interface SearchCommandDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  newTaskPath: string
  appId?: string
  agents?: SidebarChatAgent[]
  agentIds?: string[]
  appAgentsLoading?: boolean
}

/**
 * Builds chat links that stay aligned with the current shell context.
 * App routes and global routes use different chat URL patterns.
 */
function getChatPath(chat: ChatHistoryApiItem, appId?: string): string {
  // Super-agent chats embed the canonical SUPER_AGENT_ID so the URL shape is
  // identical to specialist chats — matching the sidebar history. Legacy rows
  // with no agentId are super-agent chats too, and routing them to a bare
  // `/chat/:id` would land on a route that no longer resolves.
  const agentIdForUrl =
    chat.agentId && !isSuperAgentId(chat.agentId) ? chat.agentId : SUPER_AGENT_ID
  return `/app/${appId}/agents/${agentIdForUrl}/chat/${chat.id}`
}

/**
 * Filters a chat list with a simple title-or-agent-name match.
 * This keeps search responsive without requiring another API shape.
 */
function filterChatsByQuery(
  chats: ChatHistoryApiItem[],
  query: string,
  agentsById: Map<string, SidebarChatAgent>,
): ChatHistoryApiItem[] {
  if (!query) return chats

  return chats.filter((chat) => {
    const title = (chat.title || 'New chat').toLowerCase()
    const agentName = chat.agentId ? agentsById.get(chat.agentId)?.name.toLowerCase() : ''

    return title.includes(query) || agentName?.includes(query)
  })
}

function ChatSearchAvatar({
  agent,
  isStreaming,
  iconName,
}: {
  agent?: SidebarChatAgent
  isStreaming: boolean
  iconName: string | null | undefined
}) {
  // A running chat reads as running everywhere. The spinner wins over the agent
  // avatar for the same reason it does in the sidebar rows: "is this still
  // working?" is the more urgent question than "who am I talking to".
  if (isStreaming) {
    return (
      <div className="flex size-6 shrink-0 items-center justify-center rounded-lg border border-border/70 bg-muted/60">
        <ChatRowIcon isStreaming iconName={null} className="size-3.5" />
      </div>
    )
  }

  if (agent) {
    return (
      <AgentIcon
        avatar={agent.avatar}
        name={agent.name}
        size="sm"
        rounded="lg"
        muted
        className="shrink-0"
      />
    )
  }

  // Super-agent chats have no avatar to show, so use the chat's own AI-generated
  // icon — the same one the sidebar renders — instead of one generic glyph for
  // every row.
  return (
    <div className="flex size-6 shrink-0 items-center justify-center rounded-lg border border-border/70 bg-muted/60 text-muted-foreground">
      <ChatRowIcon isStreaming={false} iconName={iconName} className="size-3.5" />
    </div>
  )
}

/**
 * SearchCommandDialog renders a centered command palette for chat discovery.
 * The content intentionally mirrors sidebar grouping so the jump feels predictable.
 */
export function SearchCommandDialog({
  open,
  onOpenChange,
  newTaskPath,
  appId,
  agents = [],
  agentIds = [],
  appAgentsLoading = false,
}: SearchCommandDialogProps) {
  const navigate = useNavigate()
  const { setOpenMobile } = useSidebar()
  const [query, setQuery] = React.useState('')

  const {
    groupedData,
    isLoading,
    isError,
    error,
    fetchChatHistory,
  } = useChatHistory({
    autoFetch: false,
    limit: SEARCH_LIMIT,
  })

  React.useEffect(() => {
    if (!open) {
      setQuery('')
      return
    }

    void fetchChatHistory({
      page: 1,
      limit: SEARCH_LIMIT,
    })
  }, [fetchChatHistory, open])

  /**
   * Same live decorations the sidebar rows use, so a chat that is running shows
   * its spinner here too and a title still being generated types in rather than
   * appearing as "New chat".
   */
  const { animatingTitles, streamingChatIds, iconOverrides } = useChatRowLiveState()

  const scopedAgentIds = React.useMemo(() => new Set(agentIds), [agentIds])
  const agentsById = React.useMemo(
    () => new Map(agents.map((agent) => [agent.id, agent])),
    [agents],
  )

  const visibleGroupedData = React.useMemo<GroupedChatData | null>(() => {
    if (!groupedData) return null
    if (!appId) return groupedData
    if (appAgentsLoading) return null

    // Super-agent chats belong to no agent row, so an agent-id scope drops
    // them: the palette silently hid every task a member had ever run. They
    // pass on the same terms the sidebar history uses — a missing agentId is a
    // super-agent chat on rows that predate the sentinel.
    const filterGroup = (items: ChatHistoryApiItem[]) =>
      items.filter(
        (chat) =>
          !chat.agentId ||
          isSuperAgentId(chat.agentId) ||
          scopedAgentIds.has(chat.agentId),
      )

    return {
      today: filterGroup(groupedData.today),
      yesterday: filterGroup(groupedData.yesterday),
      lastWeek: filterGroup(groupedData.lastWeek),
      lastMonth: filterGroup(groupedData.lastMonth),
      older: filterGroup(groupedData.older),
    }
  }, [appAgentsLoading, appId, groupedData, scopedAgentIds])

  const normalizedQuery = query.trim().toLowerCase()

  const filteredGroupedData = React.useMemo<GroupedChatData | null>(() => {
    if (!visibleGroupedData) return null

    return {
      today: filterChatsByQuery(visibleGroupedData.today, normalizedQuery, agentsById),
      yesterday: filterChatsByQuery(visibleGroupedData.yesterday, normalizedQuery, agentsById),
      lastWeek: filterChatsByQuery(visibleGroupedData.lastWeek, normalizedQuery, agentsById),
      lastMonth: filterChatsByQuery(visibleGroupedData.lastMonth, normalizedQuery, agentsById),
      older: filterChatsByQuery(visibleGroupedData.older, normalizedQuery, agentsById),
    }
  }, [agentsById, normalizedQuery, visibleGroupedData])

  const visibleGroups = React.useMemo(
    () =>
      GROUP_ORDER.map((groupKey) => ({
        groupKey,
        label: GROUP_LABELS[groupKey],
        chats: filteredGroupedData?.[groupKey] ?? [],
      })).filter((group) => group.chats.length > 0),
    [filteredGroupedData],
  )

  const handleNewTask = React.useCallback(() => {
    onOpenChange(false)
    setOpenMobile(false)
    navigate(newTaskPath)
  }, [navigate, newTaskPath, onOpenChange, setOpenMobile])

  const handleSelectChat = React.useCallback(
    (chat: ChatHistoryApiItem) => {
      onOpenChange(false)
      setOpenMobile(false)
      navigate(getChatPath(chat, appId))
    },
    [appId, navigate, onOpenChange, setOpenMobile],
  )

  const isLoadingState = isLoading || appAgentsLoading
  const emptyStateCopy = normalizedQuery
    ? `No chats found for "${query.trim()}".`
    : 'No recent chats yet.'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="gap-0 overflow-hidden border-border/60 bg-background/95 p-0 shadow-[0_32px_120px_rgba(15,23,42,0.24)] backdrop-blur-xl sm:max-w-[48rem]"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Search chats</DialogTitle>
        <DialogDescription className="sr-only">
          Browse recent chats or jump into a new task.
        </DialogDescription>
        <Command className="bg-transparent text-foreground" loop shouldFilter={false}>
          <div className="flex items-center gap-3 border-b border-border/60 px-5 py-4">
            <Search className="size-4 text-muted-foreground" />
            <CommandPrimitive.Input
              autoFocus
              className="type-body h-8 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
              onValueChange={setQuery}
              placeholder="Search chats..."
              value={query}
            />
            <button
              aria-label="Close search"
              className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => onOpenChange(false)}
              type="button"
            >
              <X className="size-4" />
            </button>
          </div>

          <CommandList className="max-h-[min(64vh,32rem)] px-3 py-3">
            <div className="space-y-4">
              <CommandGroup className="p-0">
                <CommandItem
                  className="h-auto gap-3 rounded-2xl bg-muted/70 px-4 py-3 aria-selected:bg-muted aria-selected:text-foreground"
                  onSelect={handleNewTask}
                  value="new-task"
                >
                  <div className="flex size-9 items-center justify-center rounded-xl bg-background text-foreground shadow-sm ring-1 ring-border/60">
                    <FilePlus className="size-4" />
                  </div>
                  <span className="type-body-sm font-semibold">New Task</span>
                </CommandItem>
              </CommandGroup>

              {isLoadingState && (
                <div className="space-y-4 px-1 py-1">
                  {Array.from({ length: 2 }).map((_, groupIndex) => (
                    <div key={`search-loading-group-${groupIndex}`} className="space-y-2 animate-pulse">
                      <div className="h-4 w-24 rounded bg-muted/80" />
                      {Array.from({ length: 3 }).map((__, itemIndex) => (
                        <div
                          key={`search-loading-item-${groupIndex}-${itemIndex}`}
                          className="flex items-center gap-3 rounded-xl px-3 py-2"
                        >
                          <div className="size-6 rounded-lg bg-muted/80" />
                          <div className="h-4 flex-1 rounded bg-muted/80" />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {!isLoadingState && isError && (
                <div className="rounded-2xl border border-dashed border-border/80 px-4 py-8 text-center">
                  <p className="type-body-sm text-muted-foreground">
                    {error || 'Unable to load chats right now.'}
                  </p>
                </div>
              )}

              {!isLoadingState && !isError && visibleGroups.length === 0 && (
                <div className="rounded-2xl border border-dashed border-border/80 px-4 py-8 text-center">
                  <p className="type-body-sm text-muted-foreground">{emptyStateCopy}</p>
                </div>
              )}

              {!isLoadingState && !isError && visibleGroups.map(({ groupKey, label, chats }) => (
                <CommandGroup key={groupKey} className="p-0">
                  <div className="type-label px-2 pb-2 text-muted-foreground/80">
                    {label}
                  </div>
                  <div className="space-y-1">
                    {chats.map((chat) => (
                      <CommandItem
                        key={chat.id}
                        className="h-auto gap-3 rounded-xl px-3 py-3 aria-selected:bg-muted/80 aria-selected:text-foreground"
                        onSelect={() => handleSelectChat(chat)}
                        value={`${chat.id}-${chat.title || 'New chat'}`}
                      >
                        <ChatSearchAvatar
                          agent={chat.agentId ? agentsById.get(chat.agentId) : undefined}
                          isStreaming={streamingChatIds.has(chat.id)}
                          iconName={iconOverrides[chat.id] ?? chat.metadata?.icon ?? null}
                        />
                        <span className="type-body-sm min-w-0 flex-1 truncate">
                          <ChatRowTitle
                            chatId={chat.id}
                            title={chat.title}
                            animatingTitles={animatingTitles}
                          />
                        </span>
                      </CommandItem>
                    ))}
                  </div>
                </CommandGroup>
              ))}
            </div>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
